import type { SearchOption } from './types'

import { useEffect, useState } from 'react'

/**
 * Hook to handle async search with debounced term
 * Manages loading state, results, and error handling
 *
 * @param debouncedTerm - The debounced search term
 * @param onSearch - Async function to perform search
 * @param minSearchLength - Minimum length required to trigger search
 * @param maxResults - Optional max number of results to return
 * @param showOptionsOnFocus - Whether to show options when focused without input
 * @returns Object with isLoading, results, and error state
 */
export function useSearch(
  debouncedTerm: string,
  onSearch: (term: string) => SearchOption[] | Promise<SearchOption[]>,
  minSearchLength: number,
  maxResults?: number,
  showOptionsOnFocus?: boolean,
) {
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<SearchOption[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Each effect invocation creates its own `isCancelled` in a new closure.
    // When debouncedTerm changes, React runs the previous effect's cleanup
    // (setting the *previous* closure's flag to true) before running the new
    // effect. The stale effect's promise is still in flight, but when it
    // resolves after the `await`, its `isCancelled` is already true, so the
    // state updates are skipped and only the latest search writes results.
    let isCancelled = false

    const performSearch = async () => {
      const shouldSkipSearch = showOptionsOnFocus
        ? debouncedTerm.length > 0 && debouncedTerm.length < minSearchLength
        : debouncedTerm.length < minSearchLength

      if (shouldSkipSearch) {
        setResults([])
        setError(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const searchResults = await Promise.resolve(onSearch(debouncedTerm))
        if (isCancelled) return

        const limitedResults =
          maxResults && searchResults.length > maxResults
            ? searchResults.slice(0, maxResults)
            : searchResults

        setResults(limitedResults)
      } catch (err) {
        if (isCancelled) return
        setError(err instanceof Error ? err.message : 'Search failed')
        setResults([])
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    performSearch()
    return () => {
      isCancelled = true
    }
  }, [debouncedTerm, onSearch, minSearchLength, maxResults, showOptionsOnFocus])

  return { isLoading, results, error }
}
